import { Injectable, OnDestroy } from '@angular/core'
import { BehaviorSubject, Observable, Subscription } from 'rxjs'
import { map } from 'rxjs/operators'
import { AssetRealtimeGateway } from './asset-realtime.gateway'
import { GoRealtimeClient } from './go-realtime.client'
import { RealtimeSubscriptionRegistry } from './realtime-subscription.registry'
import { TrackerEventPayload, TrackerHistoryPayloadAsset } from '@app/modules/client/map/shared/models/tracker-history-payload.model'
import { SocketState, SocketStatus } from '@app/modules/client/map/shared/abstractSocket.directive'

let seq = 0

@Injectable()
export class GoAssetSocketAdapter extends AssetRealtimeGateway implements OnDestroy {
  private readonly consumerId = `asset-${++seq}`
  private readonly stateSubject: BehaviorSubject<SocketState>
  private readonly stateSub: Subscription
  private readonly coordinates$: Observable<TrackerHistoryPayloadAsset>
  private readonly events$: Observable<TrackerEventPayload>
  socketState$: Observable<SocketState>

  constructor(
    private client: GoRealtimeClient,
    private registry: RealtimeSubscriptionRegistry,
  ) {
    super()
    this.client.connect()
    this.coordinates$ = this.client.assetPosition$.pipe(map((payload) => this.sanitize(payload)))
    this.events$ = this.client.assetEvent$
    this.stateSubject = new BehaviorSubject<SocketState>({ status: SocketStatus.Disconnect })
    this.stateSub = this.client.state$
      .pipe(map((s) => ({ status: s === 'connected' ? SocketStatus.Connect : SocketStatus.Disconnect })))
      .subscribe(this.stateSubject)
    this.socketState$ = this.stateSubject.asObservable()
  }

  isConnected(state: SocketState): boolean {
    return state.status === SocketStatus.Connect
  }

  subscribe(assetIds: number[]): void {
    this.registry.registerAssets(this.consumerId, assetIds ?? [])
  }

  getCoordinates(): Observable<TrackerHistoryPayloadAsset> {
    return this.coordinates$
  }

  getEvent(): Observable<TrackerEventPayload> {
    return this.events$
  }

  close(): void {
    this.registry.releaseAssets(this.consumerId)
  }

  ngOnDestroy(): void {
    this.stateSub.unsubscribe()
    this.stateSubject.complete()
    this.registry.releaseAssets(this.consumerId)
  }

  private sanitize(payload: TrackerHistoryPayloadAsset): TrackerHistoryPayloadAsset {
    const coordinates = (payload?.coordinates ?? []).filter((c) => Number.isFinite(c?.lat) && Number.isFinite(c?.lng))
    return { ...payload, coordinates }
  }
}
